<script lang="ts">
	import { _, json } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	
	interface PricingPlan {
		name: string;
		price: number;
		priceAnnual: number;
		period: string;
		description: string;
		features: string[];
		highlighted?: boolean;
		cta: string;
		ctaVariant: 'primary' | 'secondary' | 'ghost';
		ctaHref: string;
	}

	interface FAQ {
		question: string;
		answer: string;
	}

	interface ComparisonFeature {
		category: string;
		features: {
			name: string;
			solo: boolean | string;
			starter: boolean | string;
			professional: boolean | string;
		}[];
	}
	
	let billingPeriod = $state<'monthly' | 'annually'>('annually');
	let openFaqIndex = $state<number | null>(null);
	let showComparison = $state(false);
	
	const plans = $derived<PricingPlan[]>([
		{
			name: $_('pricing.plans.solo.name'),
			price: 15,
			priceAnnual: 12.50,
			period: $_('common.perUserMonth'),
			description: $_('pricing.plans.solo.description'),
			features: $json('pricing.plans.solo.features') as string[],
			cta: $_('pricing.plans.solo.cta'),
			ctaVariant: 'ghost',
			ctaHref: 'https://app.heyweek.com/?signup&plan=solo'
		},
		{
			name: $_('pricing.plans.starter.name'),
			price: 25,
			priceAnnual: 18,
			period: $_('common.perUserMonth'),
			description: $_('pricing.plans.starter.description'),
			features: $json('pricing.plans.starter.features') as string[],
			highlighted: true,
			cta: $_('pricing.plans.starter.cta'),
			ctaVariant: 'primary',
			ctaHref: 'https://app.heyweek.com/?signup&plan=starter'
		},
		{
			name: $_('pricing.plans.professional.name'),
			price: 49,
			priceAnnual: 38,
			period: $_('common.perUserMonth'),
			description: $_('pricing.plans.professional.description'),
			features: $json('pricing.plans.professional.features') as string[],
			cta: $_('pricing.plans.professional.cta'),
			ctaVariant: 'ghost',
			ctaHref: 'https://app.heyweek.com/?signup&plan=professional'
		}
	]);

	const comparisonFeatures = $derived<ComparisonFeature[]>([
		{
			category: $_('pricing.comparison.categories.core'),
			features: [
				{ name: $_('pricing.comparison.features.timeTracking'), solo: true, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.projects'), solo: $_('pricing.comparison.values.limited'), starter: $_('pricing.comparison.values.unlimited'), professional: $_('pricing.comparison.values.unlimited') },
				{ name: $_('pricing.comparison.features.clients'), solo: false, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.mobileApps'), solo: true, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.dataExport'), solo: true, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.teamMembers'), solo: '1', starter: $_('pricing.comparison.values.upTo5'), professional: $_('pricing.comparison.values.unlimited') }
			]
		},
		{
			category: $_('pricing.comparison.categories.productivity'),
			features: [
				{ name: $_('pricing.comparison.features.todoLists'), solo: false, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.projectTasks'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.personalInsights'), solo: true, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.teamInsights'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.workspaces'), solo: false, starter: false, professional: true }
			]
		},
		{
			category: $_('pricing.comparison.categories.reporting'),
			features: [
				{ name: $_('pricing.comparison.features.reports'), solo: $_('pricing.comparison.values.limited'), starter: $_('pricing.comparison.values.advanced'), professional: $_('pricing.comparison.values.advanced') },
				{ name: $_('pricing.comparison.features.cashflow'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.customReports'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.exportFormats'), solo: 'CSV', starter: $_('pricing.comparison.values.csvPdf'), professional: $_('pricing.comparison.values.allFormats') }
			]
		},
		{
			category: $_('pricing.comparison.categories.billing'),
			features: [
				{ name: $_('pricing.comparison.features.invoicing'), solo: false, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.clientPortal'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.customBranding'), solo: false, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.multipleCurrencies'), solo: false, starter: true, professional: true }
			]
		},
		{
			category: $_('pricing.comparison.categories.team'),
			features: [
				{ name: $_('pricing.comparison.features.peopleManagement'), solo: false, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.teams'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.workspaces'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.clientAccess'), solo: false, starter: false, professional: true }
			]
		},
		{
			category: $_('pricing.comparison.categories.support'),
			features: [
				{ name: $_('pricing.comparison.features.support'), solo: $_('pricing.comparison.values.community'), starter: $_('pricing.comparison.values.email'), professional: $_('pricing.comparison.values.priority') },
				{ name: $_('pricing.comparison.features.apiAccess'), solo: false, starter: false, professional: true },
				{ name: $_('pricing.comparison.features.dataEncryption'), solo: true, starter: true, professional: true },
				{ name: $_('pricing.comparison.features.twoFactor'), solo: false, starter: true, professional: true }
			]
		}
	]);

	const faqs = $derived<FAQ[]>([
		{
			question: $_('pricing.faq.items.changePlans.question'),
			answer: $_('pricing.faq.items.changePlans.answer')
		},
		{
			question: $_('pricing.faq.items.paymentMethods.question'),
			answer: $_('pricing.faq.items.paymentMethods.answer')
		},
		{
			question: $_('pricing.faq.items.annualDiscount.question'),
			answer: $_('pricing.faq.items.annualDiscount.answer')
		},
		{
			question: $_('pricing.faq.items.dataRetention.question'),
			answer: $_('pricing.faq.items.dataRetention.answer')
		},
		{
			question: $_('pricing.faq.items.freeTrial.question'),
			answer: $_('pricing.faq.items.freeTrial.answer')
		},
		{
			question: $_('pricing.faq.items.enterprise.question'),
			answer: $_('pricing.faq.items.enterprise.answer')
		}
	]);

	function toggleFaq(index: number) {
		openFaqIndex = openFaqIndex === index ? null : index;
	}

	function formatPrice(price: number): string {
		return price === 0 ? $_('common.free') : `$${price}`;
	}

	const currentPrice = $derived((plan: PricingPlan) => {
		if (plan.price === 0) return 0;
		return billingPeriod === 'annually' ? plan.priceAnnual : plan.price;
	});

	const savings = $derived((plan: PricingPlan) => {
		if (plan.price === 0) return 0;
		return Math.round((1 - (plan.priceAnnual * 12) / (plan.price * 12)) * 100);
	});
</script>

<svelte:head>
	<title>{$_('pricing.title')} - Heyweek</title>
	<meta name="description" content={$_('pricing.subtitle')} />
	<meta property="og:title" content="{$_('pricing.title')} - Heyweek" />
	<meta property="og:description" content={$_('pricing.subtitle')} />
</svelte:head>

<Header />

<main class="pricing-page">
	<!-- Hero Section -->
	<section class="pricing-hero">
		<div class="container">
			<div class="hero__content">
				<h1 class="hero__title">{$_('pricing.hero.title')}</h1>
				<p class="hero__subtitle">
					{$_('pricing.hero.subtitle')}
				</p>
			</div>
		</div>
	</section>

	<!-- Billing Toggle -->
	<section class="billing-section">
		<div class="container">
			<div class="billing-wrapper">
				<span class="billing-label">{$_('pricing.hero.billing.monthly')}</span>
				<button 
					class="billing-toggle"
					onclick={() => billingPeriod = billingPeriod === 'monthly' ? 'annually' : 'monthly'}
					aria-pressed={billingPeriod === 'annually'}
					aria-label="Toggle billing period"
				>
					<span class="toggle__slider {billingPeriod === 'annually' ? 'toggle__slider--active' : ''}"></span>
				</button>
				<span class="billing-label">
					{$_('pricing.hero.billing.annual')}
				</span>
			</div>
		</div>
	</section>

	<!-- Pricing Cards -->
	<section class="pricing-plans">
		<div class="container">
			<div class="plans__grid">
				{#each plans as plan, index}
					<div class="plan {plan.highlighted ? 'plan--highlighted' : ''}" style="--delay: {index * 0.1}s">
						{#if plan.highlighted}
							<div class="plan__badge">{$_('pricing.plans.starter.badge')}</div>
						{/if}
						
						<div class="plan__header">
							<h2 class="plan__name">{plan.name}</h2>
							<p class="plan__description">{plan.description}</p>
						</div>
						
						<div class="plan__pricing">
							{#if plan.price === 0}
								<div class="price">
									<span class="price__amount">$0</span>
								</div>
								<div class="price__label">{$_('common.free')} {$_('pricing.plans.free.period')}</div>
							{:else}
								<div class="price">
									<span class="price__currency">$</span>
									<span class="price__amount">{currentPrice(plan).toFixed(2).replace(/\.00$/, '')}</span>
									<span class="price__period">/{$_('common.perUserMonth').split('/')[1]}</span>
								</div>
								{#if billingPeriod === 'annually'}
									<div class="price__label">
										${(currentPrice(plan) * 12).toFixed(0)} {$_('common.billedAnnually')}
										{#if savings(plan) > 0}
											<span class="price__savings">{$_('pricing.hero.billing.saveNote').replace('up to 28%', `${savings(plan)}%`)}</span>
										{/if}
									</div>
								{/if}
							{/if}
						</div>
						
						<div class="plan__cta">
							<Button 
								variant={plan.ctaVariant}
								size="md" 
								href={plan.ctaHref}
								class="plan__button"
							>
								{plan.cta}
							</Button>
						</div>
						
						<div class="plan__features">
							<ul class="features__list">
								{#each plan.features.slice(0, 6) as feature}
									<li class="feature">
										<svg class="feature__icon" viewBox="0 0 16 16" fill="none">
											<path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
										</svg>
										<span>{feature}</span>
									</li>
								{/each}
							</ul>
						</div>
					</div>
				{/each}
			</div>

			<!-- Compare features button -->
			<div class="compare-wrapper">
				<button 
					class="compare-button"
					onclick={() => showComparison = !showComparison}
				>
					{$_('pricing.comparison.title')}
					<svg class="compare-icon {showComparison ? 'compare-icon--open' : ''}" viewBox="0 0 20 20" fill="currentColor">
						<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
					</svg>
				</button>
			</div>
		</div>
	</section>

	<!-- Feature Comparison (Collapsible) -->
	{#if showComparison}
		<section class="comparison">
			<div class="container">
				<div class="comparison__wrapper">
					<table class="comparison__table">
						<thead>
							<tr>
								<th class="table__header table__header--feature"></th>
								<th class="table__header">{$_('pricing.plans.solo.name')}</th>
								<th class="table__header table__header--highlighted">{$_('pricing.plans.starter.name')}</th>
								<th class="table__header">{$_('pricing.plans.professional.name')}</th>
							</tr>
						</thead>
						<tbody>
							{#each comparisonFeatures as category}
								<tr class="table__category-row">
									<td colspan="4" class="table__category">{category.category}</td>
								</tr>
								{#each category.features as feature}
									<tr class="table__row">
										<td class="table__cell table__cell--feature">{feature.name}</td>
										<td class="table__cell">
											{#if typeof feature.solo === 'boolean'}
												{#if feature.solo}
													<svg class="table__icon table__icon--check" viewBox="0 0 16 16" fill="none">
														<path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
													</svg>
												{:else}
													<svg class="table__icon table__icon--dash" viewBox="0 0 16 16" fill="none">
														<path d="M4 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
													</svg>
												{/if}
											{:else}
												<span class="table__value">{feature.solo}</span>
											{/if}
										</td>
										<td class="table__cell table__cell--highlighted">
											{#if typeof feature.starter === 'boolean'}
												{#if feature.starter}
													<svg class="table__icon table__icon--check" viewBox="0 0 16 16" fill="none">
														<path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
													</svg>
												{:else}
													<svg class="table__icon table__icon--dash" viewBox="0 0 16 16" fill="none">
														<path d="M4 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
													</svg>
												{/if}
											{:else}
												<span class="table__value">{feature.starter}</span>
											{/if}
										</td>
										<td class="table__cell">
											{#if typeof feature.professional === 'boolean'}
												{#if feature.professional}
													<svg class="table__icon table__icon--check" viewBox="0 0 16 16" fill="none">
														<path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
													</svg>
												{:else}
													<svg class="table__icon table__icon--dash" viewBox="0 0 16 16" fill="none">
														<path d="M4 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
													</svg>
												{/if}
											{:else}
												<span class="table__value">{feature.professional}</span>
											{/if}
										</td>
									</tr>
								{/each}
							{/each}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	{/if}

	<!-- Trust Badges -->
	<section class="trust">
		<div class="container">
			<div class="trust__grid">
				<div class="trust__item">
					<svg class="trust__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
						<path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<span>{$_('pricing.trust.trial')}</span>
				</div>
				<div class="trust__item">
					<svg class="trust__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
						<path d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
					</svg>
					<span>{$_('pricing.trust.noCard')}</span>
				</div>
				<div class="trust__item">
					<svg class="trust__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
						<path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<span>{$_('pricing.trust.cancel')}</span>
				</div>
			</div>
		</div>
	</section>

	<!-- FAQ Section -->
	<section class="faq">
		<div class="container">
			<div class="faq__content">
				<h2 class="faq__title">{$_('pricing.faq.title')}</h2>
				<div class="faq__list">
					{#each faqs as faq, index}
						<div class="faq__item">
							<button 
								class="faq__question"
								onclick={() => toggleFaq(index)}
								aria-expanded={openFaqIndex === index}
							>
								<span>{faq.question}</span>
								<svg 
									class="faq__arrow {openFaqIndex === index ? 'faq__arrow--open' : ''}" 
									viewBox="0 0 20 20" 
									fill="currentColor"
								>
									<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
								</svg>
							</button>
							{#if openFaqIndex === index}
								<div class="faq__answer">
									<p>{faq.answer}</p>
								</div>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta-section">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('pricing.cta.title')}</h2>
				<p class="cta__subtitle">{$_('pricing.cta.subtitle')}</p>
				<div class="cta__buttons">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('pricing.cta.startTrial')}
					</Button>
					<Button variant="ghost" size="lg" href="/contact">
						{$_('pricing.cta.talkSales')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</main>

<Footer />

<style>
	/* Base styles */
	.pricing-page {
		background: var(--bg-primary);
		min-height: 100vh;
	}

	.container {
		max-width: 1200px;
		margin-inline: auto;
		padding-inline: var(--container-padding);
	}

	/* Hero Section */
	.pricing-hero {
		padding-block: clamp(var(--space-16), 10vw, var(--space-24));
		border-bottom: 1px solid var(--border-light);
	}

	.hero__content {
		text-align: center;
		max-width: 700px;
		margin-inline: auto;
	}

	.hero__title {
		font-size: clamp(var(--font-size-3xl), 5vw, var(--font-size-5xl));
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}

	/* Billing Toggle */
	.billing-section {
		padding-block: var(--space-8);
		border-bottom: 1px solid var(--border-light);
	}

	.billing-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-4);
	}

	.billing-label {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		display: flex;
		align-items: center;
		gap: var(--space-2);
	}


	.billing-toggle {
		position: relative;
		width: 48px;
		height: 24px;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-full);
		cursor: pointer;
		transition: all 0.2s;
	}

	.billing-toggle:hover {
		background: var(--bg-tertiary);
	}

	.toggle__slider {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 18px;
		height: 18px;
		background: var(--text-tertiary);
		border-radius: var(--radius-full);
		transition: all 0.2s;
	}

	.toggle__slider--active {
		transform: translateX(24px);
		background: var(--text-brand);
	}

	/* Pricing Plans */
	.pricing-plans {
		padding-block: var(--space-16);
	}

	.plans__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-8);
		max-width: 1000px;
		margin-inline: auto;
	}

	.plan {
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		padding: var(--space-8);
		position: relative;
		display: flex;
		flex-direction: column;
		transition: all 0.2s;
		animation: fadeInUp 0.6s ease-out var(--delay, 0s) backwards;
	}


	.plan--highlighted {
		border-color: var(--text-brand);
		border-width: 2px;
		padding: calc(var(--space-8) - 1px);
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
	}


	.plan__badge {
		position: absolute;
		top: -10px;
		left: 50%;
		transform: translateX(-50%);
		background: var(--text-brand);
		color: white;
		padding: 4px 12px;
		border-radius: var(--radius-full);
		font-size: 12px;
		font-weight: var(--font-weight-medium);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.plan__header {
		margin-bottom: var(--space-6);
	}

	.plan__name {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.plan__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}

	.plan__pricing {
		margin-bottom: var(--space-6);
	}

	.price {
		display: flex;
		align-items: baseline;
		margin-bottom: var(--space-2);
	}

	.price__currency {
		font-size: var(--font-size-xl);
		color: var(--text-tertiary);
		margin-right: 2px;
	}

	.price__amount {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		line-height: 1;
		letter-spacing: -0.02em;
	}

	.price__period {
		font-size: var(--font-size-sm);
		color: var(--text-tertiary);
		margin-left: 4px;
	}

	.price__label {
		font-size: var(--font-size-xs);
		color: var(--text-tertiary);
		display: flex;
		align-items: center;
		gap: var(--space-2);
	}

	.price__savings {
		background: hsl(142 76% 36% / 0.1);
		color: hsl(142 76% 36%);
		padding: 2px 6px;
		border-radius: var(--radius-sm);
		font-weight: var(--font-weight-medium);
	}

	.plan__cta {
		margin-bottom: var(--space-6);
	}

	:global(.plan__button) {
		width: 100%;
		justify-content: center;
	}

	.plan--highlighted :global(.plan__button) {
		font-weight: var(--font-weight-medium);
	}

	.plan__features {
		flex: 1;
		padding-top: var(--space-6);
		border-top: 1px solid var(--border-light);
	}

	.features__list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.feature {
		display: flex;
		align-items: start;
		gap: var(--space-3);
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: var(--line-height-normal);
	}

	.feature__icon {
		width: 16px;
		height: 16px;
		color: var(--text-brand);
		flex-shrink: 0;
		margin-top: 2px;
	}

	/* Compare Button */
	.compare-wrapper {
		text-align: center;
		margin-top: var(--space-8);
	}

	.compare-button {
		background: none;
		border: none;
		color: var(--text-brand);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-md);
		transition: all 0.2s;
	}

	.compare-button:hover {
		background: var(--bg-secondary);
	}

	.compare-icon {
		width: 16px;
		height: 16px;
		transition: transform 0.2s;
	}

	.compare-icon--open {
		transform: rotate(180deg);
	}

	/* Comparison Table */
	.comparison {
		padding-bottom: var(--space-12);
		animation: slideDown 0.3s ease-out;
	}

	.comparison__wrapper {
		max-width: 1000px;
		margin-inline: auto;
		overflow-x: auto;
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
	}

	.comparison__table {
		width: 100%;
		border-collapse: collapse;
		background: var(--bg-primary);
	}

	.table__header {
		padding: var(--space-4);
		text-align: center;
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
		background: var(--bg-tertiary);
		border-bottom: 1px solid var(--border-light);
		font-size: var(--font-size-sm);
	}

	.table__header--feature {
		text-align: left;
		width: 40%;
	}

	.table__header--highlighted {
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05);
		color: var(--text-brand);
	}

	.table__category-row {
		background: var(--bg-secondary);
	}

	.table__category {
		padding: var(--space-3) var(--space-4);
		font-size: 12px;
		font-weight: var(--font-weight-medium);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--text-tertiary);
		border-bottom: 1px solid var(--border-light);
	}

	.table__row:hover {
		background: var(--bg-tertiary);
	}

	.table__cell {
		padding: var(--space-3) var(--space-4);
		text-align: center;
		border-bottom: 1px solid var(--border-light);
	}

	.table__cell--feature {
		text-align: left;
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	.table__cell--highlighted {
		background: hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.02);
	}

	.table__icon {
		width: 16px;
		height: 16px;
		margin-inline: auto;
	}

	.table__icon--check {
		color: hsl(142 76% 36%);
	}

	.table__icon--dash {
		color: var(--text-tertiary);
		opacity: 0.3;
	}

	.table__value {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	/* Trust Section */
	.trust {
		padding-block: var(--space-12);
		border-top: 1px solid var(--border-light);
		border-bottom: 1px solid var(--border-light);
	}

	.trust__grid {
		display: flex;
		justify-content: center;
		gap: var(--space-12);
		flex-wrap: wrap;
	}

	.trust__item {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	.trust__icon {
		width: 20px;
		height: 20px;
		color: var(--text-brand);
	}

	/* FAQ Section */
	.faq {
		padding-block: var(--space-20);
	}

	.faq__content {
		max-width: 700px;
		margin-inline: auto;
	}

	.faq__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-8);
		letter-spacing: -0.01em;
	}

	.faq__list {
		border-top: 1px solid var(--border-light);
	}

	.faq__item {
		border-bottom: 1px solid var(--border-light);
	}

	.faq__question {
		width: 100%;
		padding: var(--space-4) 0;
		border: none;
		background: none;
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
		text-align: left;
		cursor: pointer;
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--space-4);
		transition: color 0.2s;
	}

	.faq__question:hover {
		color: var(--text-brand);
	}

	.faq__arrow {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		transition: transform 0.2s;
		color: var(--text-tertiary);
	}

	.faq__arrow--open {
		transform: rotate(180deg);
	}

	.faq__answer {
		padding-bottom: var(--space-4);
		animation: slideDown 0.3s ease-out;
	}

	.faq__answer p {
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		font-size: var(--font-size-sm);
	}

	/* CTA Section */
	.cta-section {
		padding-block: var(--space-20);
		border-top: 1px solid var(--border-light);
		background: var(--bg-secondary);
	}

	.cta__content {
		text-align: center;
		max-width: 600px;
		margin-inline: auto;
	}

	.cta__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
		letter-spacing: -0.01em;
	}

	.cta__subtitle {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: var(--line-height-relaxed);
	}

	.cta__buttons {
		display: flex;
		justify-content: center;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	/* Animations */
	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			max-height: 0;
		}
		to {
			opacity: 1;
			max-height: 2000px;
		}
	}

	/* Responsive */
	@media (max-width: 900px) {
		.plans__grid {
			grid-template-columns: 1fr;
			max-width: 400px;
		}

		.plan--highlighted {
			transform: none;
		}


		.trust__grid {
			flex-direction: column;
			gap: var(--space-4);
			align-items: center;
		}

		.comparison__wrapper {
			font-size: var(--font-size-sm);
		}

		.table__header,
		.table__cell {
			padding: var(--space-2);
		}
	}

	@media (max-width: 640px) {
		.billing-wrapper {
			flex-direction: column;
			gap: var(--space-4);
		}

		.cta__buttons {
			flex-direction: column;
			align-items: center;
		}

		.cta__buttons :global(.btn) {
			width: 100%;
			max-width: 300px;
		}
	}
</style>